import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { useChannel } from "@/components/Messaging/useChannel";
import { Message } from "@/components/Messaging/types";
import { WEBSOCKET_MESSAGE_NAME } from "@/constants";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { Notification } from "@/queries/notifications";
import { displayToast } from "@/utils/toast";

/**
 * Hook that listens for real-time notification updates via WebSocket.
 * When a NOTIFICATION_CREATED message is received, it:
 * 1. Invalidates the notifications query to fetch fresh data
 * 2. Invalidates the changelog query for the affected entity
 * 3. Triggers automatic UI refresh via React Query
 *
 * Note: This hook must be used within a UserChannelProvider context.
 */
export const useNotificationWebSocketListener = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();

  useChannel(`user:${user.id}`, WEBSOCKET_MESSAGE_NAME.NOTIFICATION_CREATED, (message: Message) => {
    const notification = JSON.parse(message.data) as Notification;

    // Show toast notification
    const content = notification.title || notification.message;
    displayToast(
      React.createElement("span", { dangerouslySetInnerHTML: { __html: content } }),
      ToastType.notification,
      notification.link || undefined,
    );

    queryClient.invalidateQueries({
      predicate: query => query.queryKey[0] === "notifications",
    });

    queryClient.invalidateQueries({ queryKey: ["global-changelog", workspaceId] });
    queryClient.invalidateQueries({ queryKey: ["changelog"] });
    queryClient.invalidateQueries({ queryKey: ["changelog-pending-count", workspaceId] });

    // Invalidate all changelog-related queries
    // This is broad but ensures UI stays in sync when notifications arrive
    queryClient.invalidateQueries({
      predicate: query => {
        const key = query.queryKey[0];
        return (
          key === "changelog" || key === "changelogComments" || key === "changelogCommentReplies"
        );
      },
    });
  });
};
